import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";
import {
    useUpdateProductQuantityMutation,
    useRemoveProductMutation,
    useApproveOrRejectOrderMutation,
    useLazyGetApprovalOrderDetailsQuery,
} from "../../../api/endpoints/approvalOrders";
import { getCurrencySymbol } from "../../../utils/currencySymbol";

const ApprovalOrderDialog = ({
    visible,
    onHide,
    order,
    onUpdated,
    isParentDealer = false,
    selectedTab,
}) => {
    const toast = useRef(null);
    const [updateQty] = useUpdateProductQuantityMutation();
    const [removeProduct] = useRemoveProductMutation();
    const [approveReject] = useApproveOrRejectOrderMutation();
    const [triggerDetails, { data: details, isFetching }] =
        useLazyGetApprovalOrderDetailsQuery();

    const [summary, setSummary] = useState({
        dealerName: order?.dealerName ?? "",
        netAmount: order?.netAmount ?? 0,
        retailAmount: order?.retailAmount ?? 0,
        currency: order?.currency ?? "EUR",
    });

    const [selectedPaymentTerm, setSelectedPaymentTerm] = useState(null);
    const [paymentTermOptions, setPaymentTermOptions] = useState([]);
    const [isPaymentTermTouched, setIsPaymentTermTouched] = useState(false);
    const [uiQty, setUiQty] = useState({});

    const { th } = useLanguage();

    const isWaitingApproval = selectedTab === 20;
    const canModify = isParentDealer && isWaitingApproval;
    // Bülten siparişi kontrolü
    const isServiceBulletinOrder = order?.bulletNumber != null && order?.bulletNumber !== "";

    useEffect(() => {
        if (visible && order?.id) {
            // State'leri resetle
            setSelectedPaymentTerm(null);
            setPaymentTermOptions([]);
            setIsPaymentTermTouched(false);
            setUiQty({});
            setSummary({
                dealerName: order?.dealerName ?? "",
                netAmount: order?.netAmount ?? 0,
                retailAmount: order?.retailAmount ?? 0,
                currency: order?.currency ?? "EUR",
            });
            // Detayları getir
            triggerDetails(order.id);
        }
    }, [visible, order?.id]);

    useEffect(() => {
        if (details?.data) {
            const items = details.data.items ?? details.data ?? [];
            const total = items.reduce(
                (sum, item) => sum + (item.netAmount ?? 0),
                0
            );
            const currency = details.data.currency ?? items[0]?.currency ?? order?.currency ?? "EUR";
            
            setSummary({
                dealerName: details.data.dealerName ?? order?.dealerName ?? "",
                netAmount: details.data.netAmount ?? total,
                retailAmount: details.data.retailAmount ?? order?.retailAmount ?? 0,
                currency: currency,
            });

            // Ödeme tipi seçeneklerini ayarla
            if (details.data.paymentTermOptions) {
                setPaymentTermOptions(details.data.paymentTermOptions);
            }
            
            // Varsayılan ödeme tipini ayarla (sadece kullanıcı henüz değiştirmediyse)
            if (!isPaymentTermTouched && details.data.currentPaymentZterm) {
                setSelectedPaymentTerm(details.data.currentPaymentZterm);
            }
        }
    }, [details, order, isPaymentTermTouched]);

    const handleQtyChange = async (row, value) => {
        const serverQty = Number(row.quantity ?? 1);
        const desired = Number(value ?? 1);
        
        setUiQty((m) => ({ ...m, [row.itemCode]: desired }));
        
        try {
            const result = await updateQty({ orderId: order.id, itemCode: row.itemCode, quantity: desired });
            
            if (result?.error) {
                // Hata durumunda eski değere dön
                setUiQty((m) => ({ ...m, [row.itemCode]: serverQty }));
                // Toast kontrolü - modal kapatılmışsa toast null olabilir
                if (toast.current) {
                    const errorMessage = result.error?.data?.message || th("approvalOrders.toast.qtyUpdateFailed");
                    toast.current.show({ severity: "error", summary: errorMessage, life: 3000 });
                }
                return;
            }
            
            // Başarılı durumda UI state'i temizle
            setUiQty((m) => {
                const { [row.itemCode]: _ignored, ...rest } = m;
                return rest;
            });
            
            if (toast.current) {
                toast.current.show({ severity: "success", summary: th("approvalOrders.toast.qtyUpdated") });
            }
            triggerDetails(order.id);
        } catch (error) {
            // Hata durumunda eski değere dön
            setUiQty((m) => ({ ...m, [row.itemCode]: serverQty }));
            if (toast.current) {
                toast.current.show({ severity: "error", summary: th("approvalOrders.toast.qtyUpdateFailed"), life: 3000 });
            }
        }
    };

    const handleDelete = async (row) => {
        const result = await removeProduct({ orderId: order.id, itemCode: row.itemCode });
        if (toast.current) {
            toast.current.show({ severity: "info", summary: th("approvalOrders.toast.productDeleted") });
        }
        
        // Sipariş tamamen silindiyse (tüm itemlar silindiğinde) listeye yönlendir
        const isOrderDeleted = result?.data?.data?.isOrderDeleted === true || result?.data?.isOrderDeleted === true;
        if (isOrderDeleted) {
            onUpdated?.();
            onHide();
            return;
        }
        
        triggerDetails(order.id);
    };

    const handleApprove = async () => {
        // Onay için ödeme tipi zorunlu - null veya boş string kontrolü
        if (selectedPaymentTerm === null || selectedPaymentTerm === "" || selectedPaymentTerm === undefined) {
            if (toast.current) {
                toast.current.show({ 
                    severity: "error", 
                    summary: th("approvalOrders.toast.paymentTermRequired"),
                    life: 3000 
                });
            }
            return;
        }
        await approveReject({ 
            orderId: order.id, 
            isApproved: true,
            paymentZterm: selectedPaymentTerm 
        });
        if (toast.current) {
            toast.current.show({ severity: "success", summary: th("approvalOrders.toast.orderApproved") });
        }
        onUpdated?.();
        onHide();
    };

    const handleReject = async () => {
        // Red için ödeme tipi opsiyonel
        const payload = {
            orderId: order.id,
            isApproved: false,
            rejectReason: "No reason provided",
        };
        // Sadece geçerli bir değer varsa ekle
        if (selectedPaymentTerm !== null && selectedPaymentTerm !== "" && selectedPaymentTerm !== undefined) {
            payload.paymentZterm = selectedPaymentTerm;
        }
        await approveReject(payload);
        if (toast.current) {
            toast.current.show({ severity: "warn", summary: th("approvalOrders.toast.orderRejected") });
        }
        onUpdated?.();
        onHide();
    };

    const footer = canModify ? (
        <div className="flex justify-content-end gap-2">
            <Button label={th("common.reject")} icon="pi pi-times" severity="danger" onClick={handleReject} />
            <Button label={th("common.approve")} icon="pi pi-check" severity="success" onClick={handleApprove} />
        </div>
    ) : null;

    return (
        <Dialog
            header={`${th("approvalOrders.order")} #${order?.id}`}
            visible={visible}
            onHide={onHide}
            style={{ width: "70rem" }}
            footer={footer}
            modal
        >
            <Toast ref={toast} />

            <div className="grid p-3 border-1 surface-border border-round mb-3">
                <div className="col-12 md:col-6">
                    <div className="mb-2">
                        <strong>{th("approvalOrders.dealer")}:</strong> {summary.dealerName || order?.dealerName}
                    </div>
                    <div className="mb-2">
                        <strong>{th("order.retailAmount")}:</strong>{" "}
                        {Number(summary.retailAmount ?? 0).toFixed(2)} {getCurrencySymbol(summary.currency)}
                    </div>
                    <div className="mb-2">
                        <strong>{th("approvalOrders.columns.netAmount")}:</strong>{" "}
                        {Number(summary.netAmount ?? 0).toFixed(2)} {getCurrencySymbol(summary.currency)}
                    </div>
                    
                    {canModify && paymentTermOptions.length > 0 && (
                        <div className="field mt-3 mb-0">
                            <label className="font-bold block mb-2">
                                {th("order.paymentTerm")} <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={selectedPaymentTerm}
                                options={paymentTermOptions}
                                optionLabel="text"
                                optionValue="code"
                                placeholder={th("order.selectPaymentTerm")}
                                onChange={(e) => {
                                    setSelectedPaymentTerm(e.value);
                                    setIsPaymentTermTouched(true);
                                }}
                                className="w-full md:w-20rem"
                            />
                        </div>
                    )}
                    
                    {!canModify && selectedPaymentTerm && (
                        <div className="mt-2">
                            <strong>{th("order.paymentTerm")}:</strong>{" "}
                            {paymentTermOptions.find(opt => opt.code === selectedPaymentTerm)?.text || selectedPaymentTerm}
                        </div>
                    )}
                </div>

                {details?.data?.description && (
                    <div className="col-12 md:col-6">
                        <strong>{th("common.description")}:</strong>
                        <div
                            className="mt-2 p-2 surface-ground border-round"
                            style={{ maxHeight: "8rem", overflowY: "auto" }}
                        >
                            {details.data.description}
                        </div>
                    </div>
                )}
            </div>

            <DataTable
                value={details?.data?.items ?? details?.data ?? []}
                loading={isFetching}
                showGridlines
                stripedRows
                responsiveLayout="scroll"
                emptyMessage={th("common.noRecords")}
            >
                {canModify && (
                    <Column
                        header={th("common.actions")}
                        body={(row) => {
                            const disabled = isServiceBulletinOrder;
                            return (
                                <div className="flex align-items-center gap-1">
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-text p-button-danger"
                                        onClick={() => handleDelete(row)}
                                        disabled={disabled}
                                    />
                                    {disabled && (
                                        <>
                                            <i
                                                id={`delete-tooltip-approval-${row.itemCode}`}
                                                className="pi pi-info-circle text-color-secondary"
                                                style={{ cursor: "help" }}
                                            />
                                            <Tooltip
                                                target={`#delete-tooltip-approval-${row.itemCode}`}
                                                content={th("basket.serviceBulletinOrderRestriction")}
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        }}
                        style={{ width: "6rem" }}
                    />
                )}
                <Column field="itemCode" header={th("approvalOrders.columns.partCode")} sortable />
                <Column field="partName" header={th("approvalOrders.columns.partName")} sortable />
                <Column field="uom" header={th("approvalOrders.columns.unit")} sortable />
                <Column
                    header={th("approvalOrders.columns.quantity")}
                    body={(row) => {
                        const disabled = isServiceBulletinOrder;
                        const displayQty = uiQty[row.itemCode] !== undefined ? uiQty[row.itemCode] : row.quantity;
                        return canModify ? (
                            <div className="flex align-items-center gap-1">
                                <InputNumber
                                    value={displayQty}
                                    min={1}
                                    onValueChange={(e) => !disabled && handleQtyChange(row, e.value)}
                                    showButtons
                                    inputStyle={{ width: "60px", textAlign: "center" }}
                                    disabled={disabled}
                                />
                                {disabled && (
                                    <>
                                        <i
                                            id={`qty-tooltip-approval-${row.itemCode}`}
                                            className="pi pi-info-circle text-color-secondary"
                                            style={{ cursor: "help" }}
                                        />
                                        <Tooltip
                                            target={`#qty-tooltip-approval-${row.itemCode}`}
                                            content={th("basket.serviceBulletinOrderRestriction")}
                                        />
                                    </>
                                )}
                            </div>
                        ) : (
                            <span>{row.quantity}</span>
                        );
                    }}
                />
                <Column
                    field="dealerDiscountedAmount"
                    header={th("approvalOrders.columns.discountedAmount")}
                    body={(row) =>
                        `${Number(row.dealerDiscountedAmount ?? 0).toFixed(2)} ${getCurrencySymbol(row?.currency)}`
                    }
                />
                <Column
                    field="netAmount"
                    header={th("approvalOrders.columns.totalNetAmount")}
                    body={(row) =>
                        `${Number(row.netAmount ?? 0).toFixed(2)} ${getCurrencySymbol(row?.currency)}`
                    }
                />
            </DataTable>
        </Dialog>
    );
};

export default ApprovalOrderDialog;
